import { clamp } from './util.js';

const $ = (id) => document.getElementById(id);

const SUN_PATH = 'M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3M5.3 5.3l2.1 2.1M16.6 16.6l2.1 2.1M18.7 5.3l-2.1 2.1M7.4 16.6l-2.1 2.1';

/** Day speed (game hours per real second): slow through golden hour + dusk, quick at night. */
export function hourRate(h) {
  if (h >= 6 && h < 17) return 0.09;
  if (h >= 17 && h < 20) return 0.04;
  if (h >= 5 && h < 6) return 0.045;
  return 0.15;
}

export function createUI({ audio, rig, sky, van, weasel, state, takePhoto }) {
  const hud = $('hud'), intro = $('intro'), startBtn = $('startBtn');
  const toastEl = $('toast');
  let toastT = 0;

  function toast(msg, ms = 1600) {
    toastEl.textContent = msg;
    toastEl.classList.add('show');
    clearTimeout(toastT);
    toastT = setTimeout(() => toastEl.classList.remove('show'), ms);
  }

  document.querySelectorAll('.btn, .chip, [data-cam]').forEach((b) => { if (b.title) b.setAttribute('aria-label', b.title); });

  // ---------------------------------------------------------------- start
  let started = false;
  function begin() {
    if (started) return;
    started = true;
    audio.start();
    intro.classList.add('gone');
    hud.classList.remove('off');
    rig.setMode('follow');
    setCam('follow');
    setTimeout(() => $('hint').classList.add('hide'), 16000);
    audio.sfx('ding');
  }
  startBtn.addEventListener('click', begin);

  // ---------------------------------------------------------------- camera segment
  const camBtns = [...document.querySelectorAll('[data-cam]')];
  function setCam(mode) {
    rig.setMode(mode);
    camBtns.forEach((b) => b.classList.toggle('on', b.dataset.cam === mode));
  }
  camBtns.forEach((b) => b.addEventListener('click', () => { setCam(b.dataset.cam); audio.sfx('click'); }));

  // ---------------------------------------------------------------- music
  const musicBtn = $('musicBtn');
  function setMuted(m) {
    audio.setMuted(m);
    $('musicOn').classList.toggle('off', m);
    $('musicOff').classList.toggle('off', !m);
    toast(m ? 'Music off' : '♪ Music on');
  }
  musicBtn.addEventListener('click', () => { if (!audio.started) audio.start(); setMuted(!audio.muted); });

  // ---------------------------------------------------------------- honk / photo / fullscreen
  const honk = () => { if (started) van.honk(); };
  $('honkBtn').addEventListener('click', honk);
  const photo = () => {
    hud.classList.add('photo');
    $('flash').classList.add('on');
    setTimeout(() => $('flash').classList.remove('on'), 60);
    takePhoto();
    setTimeout(() => hud.classList.remove('photo'), 700);
    toast('📸 Saved a snapshot!');
  };
  $('photoBtn').addEventListener('click', photo);
  const fs = () => {
    if (!document.fullscreenElement) document.documentElement.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.();
  };
  $('fsBtn').addEventListener('click', fs);

  // ---------------------------------------------------------------- time of day
  const slider = $('timeSlider'), pop = $('timePop'), autoBtn = $('timeAuto');
  $('clockBtn').addEventListener('click', () => pop.classList.toggle('off'));
  slider.addEventListener('input', () => { sky.setHour(Number(slider.value)); state.autoTime = false; autoBtn.classList.remove('on'); });
  autoBtn.addEventListener('click', () => { state.autoTime = !state.autoTime; autoBtn.classList.toggle('on', state.autoTime); toast(state.autoTime ? 'Day cycle running' : 'Time frozen'); });

  // ---------------------------------------------------------------- keyboard
  window.addEventListener('keydown', (e) => {
    if (e.target && (e.target.tagName === 'INPUT' && e.target.type !== 'range')) return;
    const k = e.key.toLowerCase();
    if (!started) { if (k === 'enter' || k === ' ') { begin(); e.preventDefault(); } return; }
    if (k === '1') setCam('follow');
    else if (k === '2') setCam('cinematic');
    else if (k === '3') setCam('overview');
    else if (k === 'm') setMuted(!audio.muted);
    else if (k === 'h') { honk(); $('hint').classList.remove('hide'); }
    else if (k === 'p') photo();
    else if (k === 'f') fs();
    else if (k === 't') { state.autoTime = !state.autoTime; autoBtn.classList.toggle('on', state.autoTime); toast(state.autoTime ? 'Day cycle running' : 'Time frozen'); }
    else if (k === '[' || k === ']') { sky.setHour(sky.hour + (k === ']' ? 0.5 : -0.5)); toast(`Time ${fmt(sky.hour)}`); }
    else if (k === ' ') { state.paused = !state.paused; toast(state.paused ? 'Paused' : 'Resumed'); e.preventDefault(); }
    else if (k === 'r') { rig.userZoom = 1; rig.idle = 10; toast('Camera reset'); }
    else if (k === '?') $('hint').classList.toggle('hide');
  });

  // ---------------------------------------------------------------- HUD refresh
  const clockText = $('clockText'), wMood = $('wMood'), vMood = $('vMood'), chaosBar = $('chaosBar'), icon = $('clockIcon');
  let acc = 0, lastNight = -1;
  const fmt = (h) => {
    const hh = Math.floor(h), mm = Math.floor((h - hh) * 60);
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  };
  function update(dt) {
    acc += dt;
    if (acc < 0.1) return;
    acc = 0;
    clockText.textContent = fmt(sky.hour);
    if (document.activeElement !== slider) slider.value = sky.hour.toFixed(2);
    const night = sky.dayW < 0.5;
    if (night !== lastNight) {
      lastNight = night;
      icon.innerHTML = night ? '<path d="M19.5 14.5A8 8 0 1 1 9.5 4.5a6.500 6.500 0 0 0 10 10z"/>' : `<circle cx="12" cy="12" r="4.5"/><path d="${SUN_PATH}"/>`;
    }
    wMood.textContent = weasel.mood;
    chaosBar.style.width = `${Math.round(clamp(weasel.chaos, 0.06, 1) * 100)}%`;
    vMood.textContent = van.state === 'serving' ? 'serving ice cream!' : van.speed < 1 ? 'braking…' : 'cruising ♪';
  }

  return { update, toast, begin, setCam, get started() { return started; }, ready(msg = '▶ PLAY') {
    startBtn.disabled = false;
    startBtn.focus({ preventScroll: true });
    $('startLabel').textContent = msg;
    $('loadNote').textContent = 'click to start · sound on ♪';
  }, setLoading(msg) { $('loadNote').textContent = msg; } };
}
